import { Component, lazy, Suspense, type ReactNode } from "react";
import { isChunkLoadError, reportChunkError } from "@/lib/chunk-errors";
import { captureError } from "@/lib/sentry";

/**
 * Resilient loader for the support chat widget.
 *
 * A stale/missing chunk (e.g. "Failed to fetch dynamically imported module:
 * .../SupportChatWidget-*.js" after a deploy) must never break the landing page
 * or any other route. We swallow the import failure, render nothing, and report
 * it to Sentry as a `chunk_load_failure` so alerts fire on deploy-health issues.
 */
const SupportChatWidget = lazy(() =>
  import("./SupportChatWidget.tsx").catch((err) => {
    reportChunkError(err, "SupportChatWidget", { phase: "lazy-import" });
    return { default: () => null };
  }),
);

class WidgetErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    if (isChunkLoadError(error)) {
      reportChunkError(error, "SupportChatWidget", { phase: "render" });
    } else {
      console.warn("SupportChatWidget crashed and was disabled for this session.", error);
      captureError(error, { boundary: "SupportChatWidget" });
    }
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}


export default function SafeSupportChatWidget() {
  return (
    <WidgetErrorBoundary>
      <Suspense fallback={null}>
        <SupportChatWidget />
      </Suspense>
    </WidgetErrorBoundary>
  );
}
